// MapListView.tsx — the Map surface's "list" view (dashboard overhaul §5.2).
//
// The same team conversations as the graph, browsable + searchable. Like the graph's
// Source/Project/Person lenses, the list can be ORGANIZED by Project, People, or Source
// (the "Group by" toggle) and FOCUSED on a specific one (the filter dropdown — for when
// there are a lot). The project grouping carries the D6 kebab (Rename / Pin / Archive).
//
// DECLUTTER (bug-310): in Project mode the default view shows only ACTIVE projects (recent activity,
// not hidden), sorted alphabetically (pinned first). The long tail collapses into two accordions —
// ▸ ARCHIVED (manually hidden OR inactive >30d) and ▸ UNCLUSTERED (conversations with no project) —
// both closed by default, with authoritative counts so nothing is silently lost. A search or a focus
// selection falls back to a single flat list so matches surface regardless of section.

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import type { MapListParams } from '../../lib/map-list-params';
import { useConversationsSurface, type ConversationListItem } from '../../hooks/useConversationsSurface';
import { dedupeConversations } from '../../lib/conversation-pagination';
import { isProjectArchived, compareProjectName } from '../../lib/project-archive';
import { useTeamProjects, useUpdateTeamProject, type TeamProject } from '../../hooks/useTeamProjects';
import { SITE_COLOR, siteLabel } from '../../lib/sites';
import { clusterColor, buildClusterIndexMap } from '../../lib/clusters';

type GroupBy = 'project' | 'people' | 'source';
const GROUP_OPTIONS: GroupBy[] = ['project', 'people', 'source'];
const DIM_LABEL: Record<GroupBy, string> = { project: 'projects', people: 'people', source: 'sources' };

interface MapListViewProps {
  // Shared with the graph so the hidden filter + kebab state are one source of truth.
  projects: TeamProject[];
  hiddenIds: Set<string>;
  onNav: (id: string) => void;
  // Source deep-link (Sources → "View …"): a `site` value the server feed is filtered to, so the
  // list shows only that source. null = the normal all-sources list. Dismissible via the chip below.
  focusSite?: string | null;
  // URL-encoded list UI state (note-redesign PR2): seeds search/group/focus on mount, and every
  // change is reported back so MapSurface mirrors it into the URL (restore-on-return).
  initialParams?: MapListParams;
  onParamsChange?: (p: MapListParams) => void;
}

const UNCLUSTERED_ID = '__unclustered__';
const UNKNOWN_PERSON_ID = '__unknown_person__';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const AV_TONES = ['blue', 'sage', 'sand', 'lilac', 'clay', 'teal'] as const;
function avTone(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (Math.imul(h, 31) + seed.charCodeAt(i)) >>> 0;
  return AV_TONES[h % AV_TONES.length];
}

function relTime(dateStr: string): string {
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  return `${Math.floor(days / 30)}mo`;
}

interface Group {
  id: string;
  name: string;
  project?: TeamProject;
  rows: ConversationListItem[];
  people: number;
}

// One conversation row — shared by every group/section so the markup stays identical.
// Navigates to `conversations/:id` directly: this was the app's main path into a note and the last
// caller still routing through the retired `team-conversations/:id`, which only re-navigates to the
// same place (an extra mount and a duplicate analytics pageview). Keep the REDIRECT route itself —
// the backend still mints that address in Slack deep links.
function ConversationRow({ c, onNav }: { c: ConversationListItem; onNav: (id: string) => void }) {
  return (
    <div className="cv-row" onClick={() => onNav(`conversations/${c.id}`)}>
      <span className="iid mono">{c.id.slice(0, 7)}</span>
      <span className="dot" style={{ background: SITE_COLOR[c.site] ?? 'var(--faint)', width: 8, height: 8 }} title={siteLabel(c.site)} />
      <span className="ititle">{c.conversation_title || c.topic}</span>
      <span className="cv-meta">
        <span className="lab">{c.topic}</span>
        {c.author_name && (
          <span className={`av sq s20 ${avTone(c.author_name)}`}>{initials(c.author_name)}</span>
        )}
        <span className="mono" style={{ fontSize: 10, color: 'var(--faint)' }}>{relTime(c.last_captured_at)}</span>
      </span>
    </div>
  );
}

// Lazy-loaded conversation list for ONE archived project. Mounted only when its row is expanded, so the
// fetch fires on open (archived projects are stale — their conversations aren't in the main paged feed,
// so we query them directly by team_project_id). React Query caches the result, so re-expanding is free.
function ArchivedProjectConversations({ projectId, onNav }: { projectId: string; onNav: (id: string) => void }) {
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useConversationsSurface({ mode: 'team', team_project_id: projectId });
  const rows = useMemo(() => dedupeConversations(data?.pages ?? []), [data]);

  if (isFetching && rows.length === 0) {
    return <div style={{ padding: '8px 0 10px 28px', fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--faint)' }}>Loading…</div>;
  }
  if (rows.length === 0) {
    return <div style={{ padding: '8px 0 10px 28px', fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--faint)' }}>No conversations</div>;
  }
  return (
    <div className="card" style={{ padding: '5px 6px', margin: '0 0 8px 18px' }}>
      {rows.map((c, i) => (
        <div key={c.id}>
          <ConversationRow c={c} onNav={onNav} />
          {i < rows.length - 1 && <hr />}
        </div>
      ))}
      {hasNextPage && (
        <button
          onClick={() => fetchNextPage()}
          disabled={isFetchingNextPage}
          style={{
            display: 'block', width: '100%', textAlign: 'center', padding: '8px 0', marginTop: 4,
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: 'var(--faint)',
          }}
        >
          {isFetchingNextPage ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}

export function MapListView({ projects, hiddenIds, onNav, focusSite, initialParams, onParamsChange }: MapListViewProps) {
  const updateProject = useUpdateTeamProject();
  const { unclusteredCount } = useTeamProjects(); // authoritative "Unclustered (N)" count
  const now = Date.now();

  const [search, setSearch] = useState(initialParams?.search ?? '');
  const [groupBy, setGroupBy] = useState<GroupBy>(initialParams?.group ?? 'project');
  const [focusId, setFocusId] = useState<string | null>(initialParams?.focus ?? null); // narrow to one project/person/source

  // Mirror the list's UI state into the URL (via MapSurface) so a detail-page back restores it.
  // Fires once on mount with the seeded values — a no-op write (replace, identical params).
  useEffect(() => {
    onParamsChange?.({ search, group: groupBy, focus: focusId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, groupBy, focusId]);
  const [focusOpen, setFocusOpen] = useState(false);
  const focusRef = useRef<HTMLDivElement>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [unclusteredOpen, setUnclusteredOpen] = useState(false);
  const [expandedArchived, setExpandedArchived] = useState<Set<string>>(new Set());

  const toggleArchived = useCallback((id: string) => {
    setExpandedArchived(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Active project groups collapse like the archived ones: by default only the TOP project is open
  // (defaultOpen, decided by list position below), the rest show name + counts only. This map records
  // ONLY the projects the user has explicitly toggled, so their choice sticks while the untouched ones
  // keep following the "first open" default even as the feed pages in and re-sorts.
  const [projectOpen, setProjectOpen] = useState<Map<string, boolean>>(new Map());
  const toggleProjectOpen = useCallback((id: string, defaultOpen: boolean) => {
    setProjectOpen(prev => {
      const next = new Map(prev);
      const current = next.has(id) ? next.get(id)! : defaultOpen;
      next.set(id, !current);
      return next;
    });
  }, []);

  const changeGroupBy = useCallback((g: GroupBy) => {
    setGroupBy(g);
    setFocusId(null); // the focus values differ per dimension — reset on switch
  }, []);

  // REUSED data + pagination machinery (team conversations), offset-paged: useInfiniteQuery
  // ACCUMULATES fixed-size pages instead of refetching one ever-larger limit, so the list only grows
  // and never blanks mid-scroll (bug-307; also clears the silent 100-row wall from the server cap).
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage: isLoadingMore } =
    useConversationsSurface({ mode: 'team', ...(focusSite ? { site: focusSite } : {}) });
  // Dedupe by id while flattening the pages (see conversation-pagination): the feed is recency-ordered
  // and live, so a row can shift across a page boundary and arrive twice; keep the first occurrence.
  const conversations = useMemo(() => dedupeConversations(data?.pages ?? []), [data]);
  const totalCount = data?.pages[0]?.total_count ?? 0;

  // Infinite-scroll: a sentinel near the bottom fetches the next page while more pages exist.
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isLoadingMore) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isLoadingMore, fetchNextPage]);

  const searching = search.trim().length > 0;

  // Filter rows (search only — grouping replaces the old source filter), then drop hidden projects'
  // conversations (hidden projects surface in the Archived accordion via the authoritative list).
  const filtered = useMemo(() => {
    let list = conversations;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        (c.conversation_title || '').toLowerCase().includes(q) ||
        c.topic.toLowerCase().includes(q) ||
        (c.summary || '').toLowerCase().includes(q)
      );
    }
    return list.filter(c => !(c.team_project_id && hiddenIds.has(c.team_project_id)));
  }, [conversations, search, hiddenIds]);

  // Colour the project group dot by the same cluster palette the graph uses.
  const clusterIndexMap = useMemo(
    () => buildClusterIndexMap(conversations.map(c => ({ project_id: c.team_project_id ?? null }))),
    [conversations]
  );

  const groups = useMemo<Group[]>(() => {
    const byId = new Map<string, Group>();
    for (const c of filtered) {
      let id: string;
      let name: string;
      let project: TeamProject | undefined;
      if (groupBy === 'project') {
        id = c.team_project_id ?? UNCLUSTERED_ID;
        project = c.team_project_id ? projects.find(p => p.id === c.team_project_id) : undefined;
        name = project?.name ?? c.team_project_name ?? 'Unclustered';
      } else if (groupBy === 'people') {
        id = c.author_name || UNKNOWN_PERSON_ID;
        name = c.author_name || 'Unknown';
      } else {
        id = c.site;
        name = siteLabel(c.site);
      }
      let g = byId.get(id);
      if (!g) {
        g = { id, name, project, rows: [], people: 0 };
        byId.set(id, g);
      }
      g.rows.push(c);
    }
    for (const g of byId.values()) {
      g.people = new Set(g.rows.map(r => r.author_name).filter(Boolean)).size;
    }
    // Sort alphabetically by name. Project mode still pins pinned projects to the top (an explicit
    // user action) and pushes Unclustered to the very bottom.
    return Array.from(byId.values()).sort((a, b) => {
      if (groupBy === 'project') {
        if (a.id === UNCLUSTERED_ID) return 1;
        if (b.id === UNCLUSTERED_ID) return -1;
        const ap = a.project?.pinned ? 1 : 0;
        const bp = b.project?.pinned ? 1 : 0;
        if (ap !== bp) return bp - ap;
      }
      return a.name.localeCompare(b.name);
    });
  }, [filtered, projects, groupBy]);

  // Sectioned view applies only to Project mode with no active search/focus. A source deep-link
  // (focusSite) also forces the flat list — the archived/unclustered accordions fetch by
  // team_project_id WITHOUT the site filter, so sectioning them would re-introduce other sources.
  const sectioned = groupBy === 'project' && !searching && !focusId && !focusSite;

  const projectGroupArchived = useCallback(
    (g: Group) => (g.project ? isProjectArchived(g.project, now) : false),
    [now]
  );

  // ACTIVE = project groups (with loaded conversations) that aren't archived.
  const activeGroups = useMemo(
    () => (sectioned ? groups.filter(g => g.id !== UNCLUSTERED_ID && !projectGroupArchived(g)) : []),
    [sectioned, groups, projectGroupArchived]
  );

  // ARCHIVED list is authoritative (from the full projects list), so every archived project shows
  // immediately — not just ones whose old conversations happen to have paged in.
  const archivedProjects = useMemo(
    () => (sectioned ? projects.filter(p => isProjectArchived(p, now)).sort(compareProjectName) : []),
    [sectioned, projects, now]
  );

  const unclusteredGroup = useMemo(
    () => (sectioned ? groups.find(g => g.id === UNCLUSTERED_ID) : undefined),
    [sectioned, groups]
  );

  // Flat list for search / focus / people / source.
  const flatGroups = useMemo(
    () => (focusId ? groups.filter(g => g.id === focusId) : groups),
    [groups, focusId]
  );
  const focusName = focusId ? groups.find(g => g.id === focusId)?.name : null;

  const closeMenu = useCallback(() => setMenuFor(null), []);
  useEffect(() => {
    if (!menuFor) return;
    const onDoc = () => closeMenu();
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [menuFor, closeMenu]);

  // Close the focus dropdown on an outside click.
  useEffect(() => {
    if (!focusOpen) return;
    const onDoc = (e: MouseEvent) => { if (focusRef.current && !focusRef.current.contains(e.target as Node)) setFocusOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [focusOpen]);

  // Enter and Escape both unmount the rename input, which fires a trailing onBlur. committedRef
  // makes commit/cancel fire exactly once per edit (else Enter double-PATCHes, and Escape's blur
  // would otherwise COMMIT the typed value instead of cancelling). Reset when a new edit starts.
  const committedRef = useRef(false);
  const startRename = (id: string, currentName: string) => {
    committedRef.current = false;
    setRenamingId(id);
    setRenameValue(currentName);
    setMenuFor(null);
  };
  const commitRename = (project: TeamProject | undefined, currentName: string) => {
    if (committedRef.current) return;
    committedRef.current = true;
    const name = renameValue.trim();
    if (project && name && name !== currentName) {
      updateProject.mutate({ id: project.id, updates: { name } });
    }
    setRenamingId(null);
  };
  const cancelRename = () => {
    committedRef.current = true; // suppress the trailing onBlur commit
    setRenamingId(null);
  };

  const renameInput = (project: TeamProject | undefined, currentName: string) => (
    <input
      autoFocus
      className="di"
      style={{ width: 220, padding: '4px 8px', fontSize: 13 }}
      value={renameValue}
      onChange={e => setRenameValue(e.target.value)}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => {
        if (e.key === 'Enter') commitRename(project, currentName);
        if (e.key === 'Escape') cancelRename();
      }}
      onBlur={() => commitRename(project, currentName)}
    />
  );

  // Full group with its conversation rows (ACTIVE projects, people, source, flat/search/focus).
  // `collapse` is passed only for active project groups: it adds a ▸ chevron, makes the header toggle
  // open/closed, and hides the conversation card when closed. Non-collapsible callers omit it and the
  // group renders fully expanded as before.
  const renderGroup = (g: Group, collapse?: { open: boolean; onToggle: () => void }) => {
    const dotColor = groupBy === 'source'
      ? (SITE_COLOR[g.id] ?? 'var(--faint)')
      : groupBy === 'people'
        ? 'var(--inkS)'
        : g.id === UNCLUSTERED_ID
          ? 'var(--faint)'
          : clusterColor(clusterIndexMap.get(g.id) ?? 0);
    const showKebab = groupBy === 'project' && !!g.project;
    const collapsible = !!collapse;
    const open = collapse ? collapse.open : true;
    // Header counts come from the project's authoritative totals (loaded once) so they don't climb as
    // the paginated feed pages in — bug-353. People/source/unclustered groups have no project, so they
    // fall back to the derived (loaded-rows) counts.
    const convCount = g.project?.conversation_count ?? g.rows.length;
    const peopleCount = g.project?.person_count ?? g.people;
    return (
      <div className="cv-group" key={g.id}>
        <div
          className="cv-group-h"
          style={collapsible ? { cursor: 'pointer' } : undefined}
          onClick={collapsible ? () => { if (renamingId !== g.id) collapse!.onToggle(); } : undefined}
        >
          {collapsible && (
            <span style={{ display: 'inline-block', width: 10, flexShrink: 0, color: 'var(--faint)', fontSize: 10, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.12s' }}>▸</span>
          )}
          <span className="dot" style={{ background: dotColor }} />
          {renamingId === g.id ? renameInput(g.project, g.name) : <span className="gname">{g.name}</span>}
          {g.project?.user_named && renamingId !== g.id && (
            <span className="lab" style={{ fontSize: 9 }}>renamed</span>
          )}
          {g.project?.pinned && (
            <span className="lab" style={{ fontSize: 9, color: 'var(--acc)', borderColor: 'color-mix(in srgb, var(--acc) 40%, transparent)' }}>pinned</span>
          )}
          <span className="crumb">· {convCount} conversation{convCount !== 1 ? 's' : ''}{groupBy !== 'people' ? ` · ${peopleCount} ${peopleCount === 1 ? 'person' : 'people'}` : ''}</span>

          {showKebab && (
            <div style={{ marginLeft: 'auto', position: 'relative' }} onClick={e => e.stopPropagation()}>
              <button
                className="kebab"
                aria-label="Project actions"
                style={{ marginLeft: 0 }}
                onClick={e => { e.stopPropagation(); setMenuFor(menuFor === g.id ? null : g.id); }}
              >
                ⋯
              </button>
              <div className={`kmenu ${menuFor === g.id ? 'open' : ''}`} onClick={e => e.stopPropagation()}>
                <a onClick={() => startRename(g.id, g.name)}>Rename</a>
                <a onClick={() => { updateProject.mutate({ id: g.project!.id, updates: { pinned: !g.project!.pinned } }); setMenuFor(null); }}>
                  {g.project!.pinned ? 'Unpin' : 'Pin to top'}
                </a>
                <hr />
                <a className="danger" onClick={() => { updateProject.mutate({ id: g.project!.id, updates: { hidden: true } }); setMenuFor(null); }}>
                  Archive project
                </a>
              </div>
            </div>
          )}
        </div>

        {open && (
          <div className="card" style={{ padding: '5px 6px' }}>
            {g.rows.map((c, i) => (
              <div key={c.id}>
                <ConversationRow c={c} onNav={onNav} />
                {i < g.rows.length - 1 && <hr />}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Archived project: a click-to-expand header (chevron) that lazily loads its conversations on open.
  const renderArchivedProject = (p: TeamProject) => {
    const reason = p.hidden
      ? 'hidden'
      : p.last_activity_at
        ? `inactive ${relTime(p.last_activity_at)}`
        : 'no activity';
    const open = expandedArchived.has(p.id);
    return (
      <div key={p.id}>
        <div
          className="cv-group-h"
          style={{ opacity: 0.72, cursor: 'pointer' }}
          onClick={() => { if (renamingId !== p.id) toggleArchived(p.id); }}
        >
          <span style={{ display: 'inline-block', width: 10, flexShrink: 0, color: 'var(--faint)', fontSize: 10, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.12s' }}>▸</span>
          <span className="dot" style={{ background: 'var(--faint)' }} />
          {renamingId === p.id ? renameInput(p, p.name) : <span className="gname">{p.name}</span>}
          {p.user_named && renamingId !== p.id && <span className="lab" style={{ fontSize: 9 }}>renamed</span>}
          <span className="lab" style={{ fontSize: 9 }}>{reason}</span>
          <span className="crumb">· {p.conversation_count} conversation{p.conversation_count !== 1 ? 's' : ''}</span>
          <div style={{ marginLeft: 'auto', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <button
              className="kebab"
              aria-label="Project actions"
              style={{ marginLeft: 0 }}
              onClick={e => { e.stopPropagation(); setMenuFor(menuFor === p.id ? null : p.id); }}
            >
              ⋯
            </button>
            <div className={`kmenu ${menuFor === p.id ? 'open' : ''}`} onClick={e => e.stopPropagation()}>
              <a onClick={() => startRename(p.id, p.name)}>Rename</a>
              <a onClick={() => { updateProject.mutate({ id: p.id, updates: { pinned: true } }); setMenuFor(null); }}>Pin to top</a>
              {p.hidden && (
                <a onClick={() => { updateProject.mutate({ id: p.id, updates: { hidden: false } }); setMenuFor(null); }}>Unarchive</a>
              )}
            </div>
          </div>
        </div>
        {open && <ArchivedProjectConversations projectId={p.id} onNav={onNav} />}
      </div>
    );
  };

  const accordionBtn = (open: boolean, label: string, onClick: () => void) => (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
        background: 'transparent', border: 'none', cursor: 'pointer', padding: '12px 2px',
        color: 'var(--faint)', fontFamily: "'DM Mono', monospace", fontSize: 11,
        letterSpacing: '0.12em', textTransform: 'uppercase',
      }}
    >
      <span style={{ display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.12s' }}>▸</span>
      {label}
    </button>
  );

  return (
    <div>
      <div className="toolbar">
        <div className="search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" />
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={`Search ${totalCount.toLocaleString()} conversations…`}
          />
        </div>
      </div>

      {focusSite && (
        <div className="toolbar">
          <div className="chips">
            <button
              className="chip on"
              onClick={() => onNav('map?view=list')}
              title="Clear source filter"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {siteLabel(focusSite)} <span aria-hidden="true">✕</span>
            </button>
          </div>
        </div>
      )}

      <div className="toolbar">
        <div className="chips">
          <span style={{ fontSize: 10, color: 'var(--faint)', fontFamily: "'DM Mono', monospace", letterSpacing: '0.12em', textTransform: 'uppercase', marginRight: 2 }}>
            Group by
          </span>
          {GROUP_OPTIONS.map(g => (
            <button
              key={g}
              className={`chip ${groupBy === g ? 'on' : ''}`}
              style={{ textTransform: 'capitalize' }}
              onClick={() => changeGroupBy(g)}
            >
              {g}
            </button>
          ))}
        </div>

        {/* Focus filter — narrow to a specific project/person/source (graph-parallel). */}
        <div className="chips" ref={focusRef} style={{ position: 'relative', marginLeft: 'auto' }}>
          <button
            className={`chip ${focusId ? 'on' : ''}`}
            onClick={() => setFocusOpen(o => !o)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 240 }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {focusName ?? `All ${DIM_LABEL[groupBy]}`}
            </span>
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M1 1L4 4L7 1" />
            </svg>
          </button>
          {focusOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 5px)', right: 0, zIndex: 30, minWidth: 200,
              background: 'var(--panel)', border: '1px solid var(--hair2)', borderRadius: 9,
              padding: 4, boxShadow: 'var(--sig-shadow)', maxHeight: 280, overflowY: 'auto',
              display: 'flex', flexDirection: 'column', gap: 1,
            }}>
              <button
                type="button"
                onClick={() => { setFocusId(null); setFocusOpen(false); }}
                style={{
                  textAlign: 'left', border: 'none', borderRadius: 6, padding: '7px 10px', cursor: 'pointer',
                  fontSize: 12.5, fontFamily: "'DM Sans', sans-serif",
                  background: !focusId ? 'var(--acc-soft)' : 'transparent', color: !focusId ? 'var(--inkB)' : 'var(--ink)',
                }}
              >All {DIM_LABEL[groupBy]}</button>
              {groups.map(g => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => { setFocusId(g.id); setFocusOpen(false); }}
                  style={{
                    display: 'flex', justifyContent: 'space-between', gap: 12,
                    textAlign: 'left', border: 'none', borderRadius: 6, padding: '7px 10px', cursor: 'pointer',
                    fontSize: 12.5, fontFamily: "'DM Sans', sans-serif",
                    background: g.id === focusId ? 'var(--acc-soft)' : 'transparent',
                    color: g.id === focusId ? 'var(--inkB)' : 'var(--ink)',
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</span>
                  <span style={{ color: 'var(--faint)', fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{g.rows.length}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {sectioned ? (
        <>
          {activeGroups.map((g, i) => {
            const defaultOpen = i === 0; // only the very top active project starts expanded
            const isOpen = projectOpen.has(g.id) ? projectOpen.get(g.id)! : defaultOpen;
            return renderGroup(g, { open: isOpen, onToggle: () => toggleProjectOpen(g.id, defaultOpen) });
          })}
          {activeGroups.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--faint)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              No active projects
            </div>
          )}

          {archivedProjects.length > 0 && (
            <div style={{ borderTop: '1px solid var(--hair)', marginTop: 6 }}>
              {accordionBtn(archivedOpen, `Archived (${archivedProjects.length}) · hidden or inactive >30d`, () => setArchivedOpen(o => !o))}
              {archivedOpen && (
                <div className="card" style={{ padding: '4px 6px' }}>
                  {archivedProjects.map(renderArchivedProject)}
                </div>
              )}
            </div>
          )}

          {unclusteredCount > 0 && (
            <div style={{ borderTop: '1px solid var(--hair)' }}>
              {accordionBtn(unclusteredOpen, `Unclustered (${unclusteredCount.toLocaleString()})`, () => setUnclusteredOpen(o => !o))}
              {unclusteredOpen && (
                unclusteredGroup
                  ? renderGroup(unclusteredGroup)
                  : <div style={{ padding: '0 2px 14px', fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--faint)' }}>Scroll to load unclustered conversations…</div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {flatGroups.map(g => renderGroup(g))}
          {flatGroups.length === 0 && (
            <div style={{ textAlign: 'center', padding: '50px 0', fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--faint)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              No conversations match
            </div>
          )}
        </>
      )}

      {hasNextPage && <div ref={sentinelRef} style={{ height: 1 }} />}
      {isLoadingMore && (
        <div style={{ textAlign: 'center', padding: '20px 0', fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: '0.14em', color: 'var(--faint)' }}>
          LOADING MORE…
        </div>
      )}
    </div>
  );
}
